/**
 * Location trail map — MapLibre only inside pmv/map abstraction.
 */

import React, { useEffect, useRef, useState } from 'react';
import maplibregl, { type Map, type GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { getFleetMapStyleUrl } from './mapConfig';

const TRAIL_SOURCE = 'pmv-trail';
const STOPS_SOURCE = 'pmv-trail-stops';

export type TrailPoint = {
  lat: number;
  lon: number;
  observed_at?: string;
  quality?: string | null;
};

export type LocationTrailMapProps = {
  points: TrailPoint[];
  height?: number | string;
  onMapError?: (message: string) => void;
};

/** Normalize and drop illegal WGS84 pairs. Auto-swap obvious lat/lon inversions. */
export function normalizeTrailPoints(points: TrailPoint[]): Array<{ lat: number; lon: number } & TrailPoint> {
  const out: Array<{ lat: number; lon: number } & TrailPoint> = [];
  for (const p of points) {
    let lat = Number(p.lat);
    let lon = Number(p.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    // Common ingest mistake: values swapped → lon sitting in lat field (|lat| > 90)
    if (Math.abs(lat) > 90 && Math.abs(lon) <= 90) {
      const swap = lat;
      lat = lon;
      lon = swap;
    }
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) continue;
    out.push({ ...p, lat, lon });
  }
  return out;
}

export const LocationTrailMap: React.FC<LocationTrailMapProps> = ({
  points,
  height = 280,
  onMapError,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    let map: Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: getFleetMapStyleUrl(false),
        center: [54.37, 24.45],
        zoom: 11,
      });
    } catch (e) {
      setFailed(true);
      onMapError?.((e as Error).message || 'Map failed');
      return;
    }
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    mapRef.current = map;
    map.on('load', () => {
      map.addSource(TRAIL_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addSource(STOPS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'pmv-trail-line',
        type: 'line',
        source: TRAIL_SOURCE,
        paint: { 'line-color': '#2563eb', 'line-width': 3, 'line-opacity': 0.85 },
      });
      map.addLayer({
        id: 'pmv-trail-stops',
        type: 'circle',
        source: STOPS_SOURCE,
        paint: {
          'circle-radius': 5,
          'circle-color': [
            'match',
            ['get', 'role'],
            'start',
            '#1a7f37',
            'end',
            '#c0392b',
            '#6b7280',
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#fff',
        },
      });
      setReady(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const valid = normalizeTrailPoints(points);
    const line: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        valid.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: valid.map((p) => [p.lon, p.lat]),
                },
              },
            ]
          : [],
    };
    const stops: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: valid.length
        ? [
            {
              type: 'Feature',
              properties: { role: 'start' },
              geometry: { type: 'Point', coordinates: [valid[0].lon, valid[0].lat] },
            },
            ...(valid.length > 1
              ? [
                  {
                    type: 'Feature' as const,
                    properties: { role: 'end' },
                    geometry: {
                      type: 'Point' as const,
                      coordinates: [valid[valid.length - 1].lon, valid[valid.length - 1].lat],
                    },
                  },
                ]
              : []),
          ]
        : [],
    };
    try {
      const trailSrc = map.getSource(TRAIL_SOURCE) as GeoJSONSource | undefined;
      const stopsSrc = map.getSource(STOPS_SOURCE) as GeoJSONSource | undefined;
      trailSrc?.setData(line);
      stopsSrc?.setData(stops);
      if (valid.length) {
        const b = new maplibregl.LngLatBounds(
          [valid[0].lon, valid[0].lat],
          [valid[0].lon, valid[0].lat]
        );
        for (let i = 1; i < valid.length; i++) {
          b.extend([valid[i].lon, valid[i].lat]);
        }
        if (!b.isEmpty()) {
          map.fitBounds(b, { padding: 40, maxZoom: 14, duration: 400 });
        }
      }
    } catch (e) {
      // Never crash Passport — history table remains usable
      onMapError?.((e as Error).message || 'Trail bounds failed');
    }
  }, [points, ready]);

  if (failed) {
    return (
      <div
        role="region"
        aria-label="Location trail map unavailable"
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f5f5f5',
          border: '1px solid #e5e5e5',
          padding: 12,
          textAlign: 'center',
        }}
      >
        Trail map imagery unavailable — use the location history table.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      role="img"
      aria-label="Equipment location trail map"
      style={{
        height,
        width: '100%',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid #e5e5e5',
      }}
    />
  );
};

export default LocationTrailMap;
